// src/hooks/useOutsideClick.ts
import { useEffect, RefObject } from 'react';

/**
 * Custom hook that handles clicks outside of a specified element
 * @param ref Reference to the element to detect outside clicks
 * @param handler Function to call when an outside click is detected
 */
const useOutsideClick = <T extends HTMLElement = HTMLElement>(
	ref: RefObject<T | null>,
	handler: (event: MouseEvent | TouchEvent) => void
) => {
	useEffect(() => {
		const listener = (event: MouseEvent | TouchEvent) => {
			// Do nothing if clicking ref's element or descendent elements
			if (!ref.current || ref.current.contains(event.target as Node)) {
				return;
			}

			handler(event);
		};

		document.addEventListener('mousedown', listener);
		document.addEventListener('touchstart', listener);

		return () => {
			document.removeEventListener('mousedown', listener);
			document.removeEventListener('touchstart', listener);
		};
	}, [ref, handler]);
};

export default useOutsideClick;